import {useMemo} from 'react';
import {keepPreviousData, useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {getBootstrapData} from '@common/core/bootstrap-data/use-backend-bootstrap-data';
import {PublicDocsIndex} from '@app/admin/docs/public-doc';
import {BackendResponse} from '@common/http/backend-response/backend-response';

export interface PublicDocArticle {
  path: string;
  slug: string;
  title: string;
  category: string;
  html: string;
}

export interface FetchPublicDocumentationResponse extends BackendResponse {
  catalog: PublicDocsIndex;
  articles: Record<string, PublicDocArticle>;
  article: PublicDocArticle | null;
}

interface ArticleResponse extends BackendResponse {
  article: PublicDocArticle;
}

function articleSlugFromUrl(urlSlug: string): string {
  return urlSlug.replace(/^\/+|\/+$/g, '').replace(/\.md$/i, '') || 'index';
}

function fetchCatalog(): Promise<PublicDocsIndex> {
  return apiClient.get('documentation').then(r => r.data as PublicDocsIndex);
}

function fetchArticle(urlSlug: string): Promise<PublicDocArticle> {
  const apiSlug = urlSlug === 'index' ? '' : urlSlug;
  const path = apiSlug
    ? `documentation/pages/${apiSlug}`
    : 'documentation/pages';
  return apiClient
    .get(path)
    .then(r => (r.data as ArticleResponse).article);
}

function getBootstrapArticles(): Record<string, PublicDocArticle> | undefined {
  return getBootstrapData().loaders?.publicDocs?.articles;
}

export function usePublicDocsCatalog() {
  return useQuery({
    queryKey: ['documentation'],
    queryFn: fetchCatalog,
    initialData: () => getBootstrapData().loaders?.publicDocs?.catalog,
    staleTime: 60_000,
  });
}

export function usePublicDocArticle(urlSlug: string) {
  const articleSlug = articleSlugFromUrl(urlSlug);
  const bootstrapArticles = getBootstrapArticles();
  const cached = bootstrapArticles?.[articleSlug];

  const query = useQuery({
    queryKey: ['documentation', articleSlug],
    queryFn: () => fetchArticle(articleSlug),
    enabled: !cached,
    initialData: cached,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });

  return useMemo(
    () => ({
      ...query,
      data: cached ?? query.data,
      isLoading: !cached && query.isLoading && !query.data,
      isError: !cached && query.isError && !query.data,
    }),
    [cached, query],
  );
}
